import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { OrgTabs } from "@/components/OrgTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOrg } from "@/hooks/useOrg";
import { api, apiErr } from "@/lib/api";

export default function OrgMembers() {
  const { slug } = useParams();
  const { org } = useOrg(slug);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [inviting, setInviting] = useState(false);

  const isAdmin = org && (org.role === "owner" || org.role === "admin");

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/orgs/${slug}/members`);
      setMembers(data);
    } catch (e) {
      toast.error("Could not load members");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async () => {
    if (!email.trim()) {
      toast.error("Enter an email");
      return;
    }
    setInviting(true);
    try {
      await api.post(`/orgs/${slug}/invites`, { email: email.trim(), role });
      toast.success(`Invited ${email.trim()}`);
      setEmail("");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not send invite");
    } finally {
      setInviting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/orgs/${slug}/members/${id}`);
      toast.success("Member removed");
      load();
    } catch (e) {
      toast.error(apiErr(e, "Could not remove member"));
    }
  };

  return (
    <AppShell>
      <OrgTabs slug={slug} />
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-[color:var(--rb-accent)]" />
        <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">
          Members{org ? ` — ${org.name}` : ""}
        </h1>
      </div>

      {isAdmin && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4">
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@company.com"
            data-testid="invite-email-input"
            className="max-w-xs border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]"
          />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="invite-role-select" className="w-32 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]">
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={invite}
            disabled={inviting}
            data-testid="invite-submit-button"
            className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]"
          >
            {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><UserPlus className="mr-1.5 h-4 w-4" /> Invite</>)}
          </Button>
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-2xl border border-[color:var(--rb-border)]">
        {loading ? (
          <div className="p-6 text-center text-sm text-[color:var(--rb-text-faint)]">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--rb-code)] text-xs text-[color:var(--rb-text-faint)]">
              <tr>
                <th className="px-4 py-2 text-left">Member</th>
                <th className="px-4 py-2 text-left">Role</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} data-testid="member-row" className="border-t border-[color:var(--rb-border)]">
                  <td className="px-4 py-2 text-[color:var(--rb-text)]">{m.username ? `@${m.username}` : (m.email || "—")}</td>
                  <td className="px-4 py-2 text-[color:var(--rb-text-dim)]">{m.role}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        m.status === "active"
                          ? "bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]"
                          : "bg-[color:rgba(212,175,55,0.14)] text-[color:var(--rb-gold)]"
                      }`}
                    >
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    {isAdmin && m.role !== "owner" && (
                      <button
                        onClick={() => remove(m.id)}
                        data-testid="remove-member-button"
                        className="text-[color:var(--rb-text-faint)] hover:text-[color:#f87171]"
                        title={m.status === "active" ? "Remove member" : "Revoke invite"}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-xs text-[color:var(--rb-text-faint)]">No members yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </AppShell>
  );
}
